// Types for the platform SuperAdmin dashboard (cross-organization).

export type ScorecardDivision = 'sales' | 'customer_success' | 'development' | 'finance';
export type ScorecardView = 'executive' | ScorecardDivision;
export type ScorecardCadence = 'weekly' | 'monthly';
export type ScorecardUnit = 'count' | 'currency' | 'percent' | 'hours';
export type ScorecardStatus = 'missing' | 'in_progress' | 'needs_review' | 'not_applicable' | 'no_target' | 'on_track' | 'off_track';

export interface ScorecardStaff { id: number; name: string }

export interface ScorecardMetric {
  key: string;
  name: string;
  division: ScorecardDivision;
  executive_order: number | null;
  order: number;
  unit: ScorecardUnit;
  cadence: ScorecardCadence;
  definition: string;
  formula: string;
  source_help: string;
  suggested_owner: string;
  collector: string;
  owner: number | null;
  owner_name: string;
  owner_active: boolean;
  target: string | null;
  direction: 'gte' | 'lte';
  revision: number;
}

export interface ScorecardMetricSnapshot {
  name: string; definition: string; formula: string; unit: ScorecardUnit; cadence: ScorecardCadence;
  revision: number; target: string | null; direction: 'gte' | 'lte'; owner: number | null; owner_name: string;
}

export interface ScorecardObservation {
  id: number;
  period_start: string;
  value: string | null;
  numerator: string | null;
  denominator: string | null;
  not_applicable: boolean;
  note: string;
  source: 'manual' | 'records';
  source_note: string;
  evidence_url: string;
  metric_snapshot: ScorecardMetricSnapshot;
  evidence: { label: string; detail: string; path: string }[];
  as_of: string;
  finalized: boolean;
  revision: number;
  recorded_by_name: string;
  status: ScorecardStatus;
}

export interface ScorecardRow extends ScorecardMetric {
  observations: Record<string, ScorecardObservation>;
  open_issue_count: number;
}

export interface ScorecardBoard {
  cadence: ScorecardCadence;
  period_start: string;
  periods: string[];
  current_period: string;
  period_closed: boolean;
  timezone: string;
  week_starts_on: string;
  rows: ScorecardRow[];
  staff: ScorecardStaff[];
}

export interface ScorecardReadingInput {
  period_start: string; revision: number; value: string | null; numerator: string | null; denominator: string | null;
  not_applicable: boolean; finalized: boolean; source_note: string; evidence_url: string; note: string; reason: string;
}

export interface ScorecardIssueInput {
  period_start: string; title: string; next_action: string; owner: number; due_on: string;
}

export interface ScorecardIssue extends ScorecardIssueInput {
  id: number; metric_key: string; metric_name: string; owner_name: string; resolved_at: string | null;
  resolution: string; revision: number; created_at: string;
}

export interface ScorecardAudit {
  id: number; kind: string; period_start: string | null; actor_name: string; reason: string;
  before: Record<string, unknown>; after: Record<string, unknown>; created_at: string;
}

export interface ScorecardDetail {
  metric: ScorecardMetric; observation: ScorecardObservation | null; issues: ScorecardIssue[]; audit: ScorecardAudit[];
}

export interface QualifiedOnboardingCallInput {
  organization: string; held_at: string; facilitator: number; participant: string; qualification_notes: string; next_step: string;
}

export interface QualifiedOnboardingCall extends Omit<QualifiedOnboardingCallInput, 'organization' | 'facilitator'> {
  id: string; organization: string | null; organization_uuid: string; organization_name: string;
  facilitator: number | null; facilitator_name: string; voided_at: string | null; void_reason: string; created_at: string;
}
// Mirrors apexos/apps/core/admin_serializers.py.

export type OrgStaleness = 'active' | 'at_risk' | 'stale';

export interface AccountCXSummary {
  customer_id: string | null; owner_name: string | null; completed_steps: number; total_steps: number;
  next_step: string | null; onboarding_status: 'not_started' | 'in_progress' | 'blocked' | 'complete';
  health_band: HealthBand | null; health_score: number | null; health_date: string | null; open_tasks: number; overdue_tasks: number;
  next_task: string | null; next_due: string | null;
}
export interface AccountUser {
  id: number; username: string; name: string; email: string; phone: string; role: string;
  is_active: boolean; can_impersonate: boolean; last_active: string | null;
  organization: { id: string; name: string };
}
export interface AdminOrganizationListItem {
  cx?: AccountCXSummary;
  id: string;
  name: string;
  created_at: string;
  owner_name: string | null;
  owner_email: string;
  subscription_status: string | null;
  plan_label: string;
  is_free: boolean | null;
  mrr: number;
  seat_count: number | null;
  user_count: number;
  open_ticket_count: number;
  last_activity_at: string | null;
  staleness: OrgStaleness;
}

export interface AdminOrgSubscription {
  status: string;
  is_free: boolean;
  seat_count: number;
  price_per_seat: number;
  seat_discount: number;
  effective_price_per_seat: number;
  total_monthly_cost: number;
  billing_cycle: string;
  trial_end: string | null;
  current_period_end: string | null;
  cancel_at_period_end: boolean;
  cancellation_effective_at: string | null;
  pause_collection: { behavior?: string; resumes_at?: number };
  stripe_customer_id: string | null;
  stripe_subscription_id: string | null;
}

export interface AdminOrgActivityEvent {
  timestamp: string;
  event_type: string;
  description: string;
  severity: string;
  user: string | null;
}

export interface AdminOrganizationDetail extends AdminOrganizationListItem {
  owner_first_name: string;
  owner_last_name: string;
  owner_phone: string;
  company_email: string;
  company_phone_number: string;
  website: string | null;
  main_office_city: string;
  main_office_state: string;
  main_office_zip: string;
  contact_count: number;
  job_count: number;
  total_ticket_count: number;
  subscription: AdminOrgSubscription | null;
  recent_activity: AdminOrgActivityEvent[];
}

export interface AdminPlatformSummary {
  total_organizations: number;
  active_count: number;
  trial_count: number;
  past_due_count: number;
  free_count: number;
  total_mrr: number;
  avg_mrr_per_org: number;
  total_active_users: number;
  stale_org_count: number;
  open_ticket_count: number;
  new_orgs_this_month: number;
}

export interface AdminOrganizationListParams {
  account_view?: string;
  search?: string;
  status?: string;
  staleness?: OrgStaleness | '';
  has_open_tickets?: boolean;
  ordering?: string;
  page?: number;
  page_size?: number;
}

export interface PaginatedResult<T> {
  count: number;
  results: T[];
  next: string | null;
  previous: string | null;
}

// ---------------------------------------------------------------------------
// Admin hub (Apex-internal tools) — mirrors apexos/apps/admin_hub/serializers.py
//
// These describe Apex's OWN data (our staff, our boards), which lives in
// dedicated internal tables rather than in any customer's tenant rows.
// ---------------------------------------------------------------------------

export type StaffFunction = 'sales' | 'cs' | 'dev' | 'admin';

export interface StaffProfile {
  id: number;
  user_id: number;
  name: string;
  email: string;
  display_name: string;
  function: StaffFunction;
  receives_lead_assignments: boolean;
  receives_cs_alerts: boolean;
  receives_dev_updates: boolean;
  is_active: boolean;
  /**
   * Whether this person can actually be handed work right now. False when the
   * profile is active but the account has lost superuser access — the Staff
   * panel shows this so nobody wonders why their leads stopped arriving.
   */
  is_routable: boolean;
  created_at: string;
  updated_at: string;
}

export type PlatformBoardKind = 'crm' | 'dev';

export interface PlatformBoardStatus {
  id: number;
  board: number;
  name: string;
  order: number;
  color: string;
  is_done: boolean;
  /**
   * CRM boards: dropping a card in this column sets the contact's lifecycle
   * stage. Empty means the column carries no lifecycle meaning. There is no
   * `is_won` flag — "won" is `lifecycle_stage: 'activated'`.
   */
  lifecycle_stage: string;
  notify_cs: boolean;
  wip_limit: number | null;
}

export interface PlatformBoard {
  id: number;
  kind: PlatformBoardKind;
  name: string;
  color: string;
  is_default: boolean;
  statuses: PlatformBoardStatus[];
  created_at: string;
  updated_at: string;
}

export interface BoardStatusOrder {
  id: number;
  order: number;
}

/**
 * Contact lifecycle. Mirrors `apexos/apps/admin_hub/lifecycle.py`.
 *
 * The first four are a forward path; `lost` and `churned` are terminal exits,
 * not steps five and six — Activated → Churned is not progress.
 */
export type LifecycleStage =
  | 'lead'
  | 'qualifying'
  | 'trialing'
  | 'activated'
  | 'lost'
  | 'churned';

/** The four stages the progress bar renders, in order. */
export const FORWARD_LIFECYCLE_STAGES: LifecycleStage[] = [
  'lead',
  'qualifying',
  'trialing',
  'activated',
];

export const TERMINAL_LIFECYCLE_STAGES: LifecycleStage[] = ['lost', 'churned'];

/** A prospect or customer of Apex. Mirrors `admin_hub/serializers.py::LeadSerializer`. */
export type SalesQualification = Partial<Record<
  'interest_reason' | 'current_tools' | 'work_type' | 'frustrations' | 'is_owner' |
  'time_in_business' | 'priorities' | 'integrations' | 'user_count' | 'timeline' | 'notes', string>>;

export interface Lead {
  sales_qualification?: SalesQualification;
  /** The configured CRM board containing this contact, or null if unassigned. */
  board: number | null;
  /** The customer this lead became, by name. Empty until they sign up. */
  organization_name: string;
  id: string;
  display_name: string;
  first_name: string;
  last_name: string;
  email: string | null;
  phone: string;
  time_zone?: string;
  time_zone_source?: 'manual' | 'browser' | '';
  company_name: string;
  title: string;
  team_size: string;
  website: string;

  lifecycle_stage: LifecycleStage;
  stage_changed_at: string | null;
  stage_source: string;
  stage_manual_override_at: string | null;
  activated_at: string | null;
  reactivated_at: string | null;
  churned_at: string | null;
  lost_at: string | null;
  lost_reason: string;
  churn_reason: string;
  past_due_since: string | null;
  days_past_due: number | null;
  /** What the subscription state implies, shown alongside the stored stage rather than overriding it. */
  derived_stage: LifecycleStage | null;
  derived_stage_reason: string;

  is_spam: boolean;
  spam_score: number | null;

  board_status: number | null;
  position: number;
  owner: number | null;
  owner_name: string;
  assigned_at: string | null;

  source: string;
  page_url: string;
  referrer_url: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;

  estimated_seats: number | null;
  estimated_arr: string | null;
  expected_close_date: string | null;

  converted_organization: string | null;
  is_customer_inquiry: boolean;
  matched_organization: string | null;

  last_contacted_at: string | null;
  last_outreach_at?: string | null;
  last_outreach_kind?: 'call' | 'meeting' | 'email' | null;
  next_followup_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface LeadStageChange {
  id: number;
  from_stage: LifecycleStage | '';
  to_stage: LifecycleStage;
  source: string;
  reason: string;
  changed_by: number | null;
  changed_by_name: string;
  duration_in_previous_stage: string | null;
  created_at: string;
}

export interface LeadListParams {
  search?: string;
  /** Every lead across one board's columns, in one request. */
  board?: number;
  lifecycle_stage?: LifecycleStage;
  board_status?: number;
  owner?: number;
  is_customer_inquiry?: boolean;
  include_spam?: boolean;
  page?: number;
  page_size?: number;
}

/** A development ticket. Mirrors `admin_hub/serializers.py::DevTicketSerializer`. */
export interface DevTicketSupportLink {
  id: number;
  dev_ticket: number;
  support_ticket: string;
  ticket_number: string;
  subject: string;
  ticket_status: string;
  organization_name: string;
  notify_customer: boolean;
  customer_informed: boolean;
  linked_by: number | null;
  created_at: string;
}

export type DevTicketType = 'bug' | 'feature' | 'improvement' | 'chore';
export type DevTicketPriority = 'low' | 'normal' | 'high' | 'urgent';
export type DevTicketSeverity = 'cosmetic' | 'minor' | 'major' | 'blocking';

export interface DevTicket {
  board: number | null;
  tags: string[];
  due_date: string | null;
  attachments: DevTicketAttachment[];
  checklist: DevTicketChecklistItem[];
  expected_updated_at?: string;
  /** The column it sits in, by name — so screens stop re-deriving it from the id. */
  board_status_name: string;
  id: number;
  /** Display form, e.g. DEV-42. */
  reference: string;
  title: string;
  description: string;
  ticket_type: DevTicketType;
  priority: DevTicketPriority;
  severity: DevTicketSeverity;
  board_status: number | null;
  position: number;
  assigned_to: number | null;
  assigned_to_name: string;
  reporter: number | null;
  reporter_name?: string;
  affected_organizations: string[];
  linked_support_tickets: DevTicketSupportLink[];
  release_version: string;
  changelog_type: string;
  changelog_text: string;
  /** Ready to paste into a changelog.d file. */
  changelog_fragment: string;
  external_url: string;
  estimate_points: number | null;
  status_changed_at: string | null;
  shipped_at: string | null;
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface DevTicketListParams {
  tag?: string;
  archived?: boolean;
  search?: string;
  board?: number;
  ticket_type?: DevTicketType;
  priority?: DevTicketPriority;
  assigned_to?: number;
  organization?: string;
  page?: number;
  page_size?: number;
}

export type HealthBand = 'healthy' | 'at_risk' | 'stalled';

export interface OrgHealthSnapshot {
  taken_on: string;
  score: number;
  band: HealthBand;
  signals: Record<string, boolean>;
  /**
   * Raw per-organization counts. Mostly numbers, plus `setup_missing`, the
   * list of wizard steps still outstanding — named so a rep can say which
   * ones rather than only that setup is unfinished.
   */
  counts: Record<string, number | string[]>;
  days_since_activity: number | null;
  open_ticket_count: number;
}

/** The eight Quick Setup steps, keyed as `SetupProgress.STEP_CHOICES` names them. */
export const SETUP_STEP_LABELS: Record<string, string> = {
  opportunity_boards: 'Opportunity boards',
  job_boards: 'Job boards',
  marketing_photos: 'Marketing photos',
  knowledge_graph: 'Knowledge graph',
  gbp_connection: 'Google Business Profile',
  contract_templates: 'Contract templates',
  team_invitations: 'Team invitations',
  services: 'Services',
};
export const SETUP_TOTAL_STEPS = 8;

export interface CustomerRecord {
  sales_qualification?: SalesQualification;
  id: string;
  organization: string;
  organization_name: string;
  organization_created_at: string;
  lead: string | null;
  is_internal: boolean;
  cs_owner: number | null;
  cs_owner_name: string;
  do_not_email: boolean;
  snoozed_until: string | null;
  last_nudged_at: string | null;
  nudge_log: Record<string, string>;
  notes: string;
  latest_health: OrgHealthSnapshot | null;
  /** What is missing, worst-first — the shortlist for a call. */
  needs_attention: string[];
  open_task_count: number;
  created_at: string;
  updated_at: string;
}

export interface StaffTask {
  id: string;
  /** The CustomerRecord this belongs to, for opening the onboarding drawer. */
  customer: string;
  title: string;
  notes: string;
  kind: string;
  organization: string | null;
  organization_name: string;
  lead: string | null;
  lead_name?: string;
  dev_ticket: number | null;
  assignee: number | null;
  assignee_name: string;
  due_at: string | null;
  completed_at: string | null;
  created_by_rule: string;
  is_open: boolean;
  is_overdue: boolean;
  created_at: string;
}

/** One row on a client's timeline. Mirrors `PlatformActivitySerializer`. */
export interface PlatformActivity {
  id: string;
  kind: string;
  lead: string | null;
  organization_id: string | null;
  occurred_at: string;
  actor: number | null;
  actor_name: string;
  summary: string;
  body: string;
  metadata: Record<string, unknown>;
  is_editable: boolean;
  support_ticket: string | null;
  support_ticket_detail: {
    id: string;
    ticket_number: string;
    subject: string;
    status: string;
    priority: string;
    is_open: boolean;
  } | null;
  stage_change: number | null;
  submission: string | null;
  created_at: string;
}

/** Somebody who can be assigned work. Shared by every assignee picker. */
export interface AssignableStaff {
  id: number;
  name: string;
  email: string;
  function: string;
}

export interface DevTicketComment {
  attachments: DevTicketAttachment[];
  can_edit: boolean;
  id: number;
  dev_ticket: number;
  author: number | null;
  author_name: string;
  body: string;
  created_at: string;
  updated_at: string;
}

export interface DevTicketAttachment {
  id: number;
  name: string;
  content_type: string;
  size: number;
  comment: number | null;
  uploaded_by_name: string;
  created_at: string;
  download_url: string;
}

export interface DevTicketChecklistItem {
  id: number;
  text: string;
  is_done: boolean;
}

/** A staff member's own connected mailbox. */
export interface StaffEmailAccount {
  source?: 'profile' | 'staff';
  id: string;
  email_address: string;
  provider: string;
  is_active: boolean;
  /** False when the refresh token is missing, so sending would fail. */
  can_send: boolean;
  last_sync_error: string;
  created_at: string;
}

/** A reusable email body a sequence step sends. */
export interface PlatformEmailTemplateFolder {
  id: string;
  name: string;
}

export interface PlatformEmailTemplate {
  folder?: string | null;
  id: string;
  name: string;
  subject: string;
  body_text: string;
  body_html: string;
  created_at: string;
  updated_at: string;
}

/**
 * One email in a sequence. `day_offset` counts from the customer's signup, not
 * from enrolment, so a customer enrolled late starts partway through.
 */
export interface OnboardingStep {
  id: string;
  sequence: string;
  order: number;
  day_offset: number;
  template: string;
  template_name: string;
  /** Health signal that, once true, makes this step unnecessary. */
  skip_if_signal: string;
}

export interface OnboardingSequence {
  id: string;
  name: string;
  is_active: boolean;
  auto_enrol: boolean;
  from_name: string;
  reply_to: string;
  send_after_hour: number;
  send_before_hour: number;
  steps: OnboardingStep[];
  enrolled_count: number;
  created_at: string;
  updated_at: string;
}

export type EnrollmentStatus = 'active' | 'completed' | 'cancelled' | 'suppressed';

export interface OnboardingEnrollment {
  id: string;
  sequence: string;
  sequence_name: string;
  customer: string;
  customer_name: string;
  status: EnrollmentStatus;
  steps_run: number;
  enrolled_at: string;
  completed_at: string | null;
}

export interface NpsCampaign {
  id: string;
  name: string;
  is_active: boolean;
  question_text: string;
  followup_text: string;
  min_days_since_signup: number;
  response_cooldown_days: number;
  snooze_days: number;
  max_prompted_users_per_org: number;
  response_count: number;
  created_at: string;
}

export type NpsCategory = 'promoter' | 'passive' | 'detractor';

export interface NpsResponse {
  id: string;
  score: number;
  category: NpsCategory;
  comment: string;
  organization_uuid: string | null;
  organization_name: string;
  respondent: string;
  resolved: boolean;
  resolved_at: string | null;
  resolution_notes: string;
  submitted_at: string;
}

/** `/admin/nps/responses/summary/`. `score` is null until someone answers. */
export interface NpsSummary {
  score: number | null;
  total: number;
  promoters: number;
  passives: number;
  detractors: number;
  unresolved_detractors?: number;
}

/**
 * What `/admin/crm/import/` did, or — with `dry_run` — what it would do.
 *
 * A dry run performs the real import and rolls it back, so these numbers are
 * the outcome rather than an estimate: the preview and the commit cannot
 * disagree.
 */
export interface ApexCrmImportReport {
  dry_run: boolean;
  exported_at: string;
  leads_created: number;
  leads_updated: number;
  leads_skipped: number;
  stages_applied: number;
  notes_imported: number;
  activities_imported: number;
  emails_imported: number;
  /** Rows a previous run already wrote; the re-run recognised and skipped them. */
  timeline_already_present: number;
  timeline_total: number;
  /** Decimal string — annualised, so it does not lose cents to a float. */
  pipeline_arr: string;
  /** Old CRM user name → the ApexOS account their records were attributed to. */
  owners_matched: Record<string, string>;
  warnings: string[];
}

export interface OrganizationCXProfile {
  customer: CustomerRecord | null;
  leads: { id: string; name: string }[];
}
export interface OrganizationTextOptions {
  permission_detail?: string;
  sender_detail?: string;
  sender_code?: string;
  to_number: string;
  allowed: boolean;
  permission: { allowed?: boolean; number?: string; note?: string; recorded_at?: string };
  senders: { number: string; label: string }[];
}
